/**
 * Shared office-hours helpers. Property feeds often omit weekend (or other
 * closed) rows entirely — every hours surface should still list those days
 * as Closed instead of dropping them.
 */

export interface OfficeHourLike {
  day?: string | null;
  open_time?: string | null;
  close_time?: string | null;
}

export const WEEK_DAY_KEYS = ['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const;

const WEEK_DAY_LABELS: Record<(typeof WEEK_DAY_KEYS)[number], string> = {
  MON: 'Mon',
  TUE: 'Tue',
  WED: 'Wed',
  THU: 'Thu',
  FRI: 'Fri',
  SAT: 'Sat',
  SUN: 'Sun',
};

function dayKey(day: string | null | undefined): string {
  return (day || '').trim().toUpperCase().slice(0, 3);
}

/**
 * Return a Mon–Sun list, filling any weekday missing from `hours` as Closed.
 * Returns an empty array when there are no hours to display.
 */
export function withClosedDays<T extends OfficeHourLike>(
  hours: T[] | null | undefined,
): T[] {
  if (!hours?.length) return [];

  const byDay = new Map<string, T>();
  for (const entry of hours) {
    const key = dayKey(entry.day);
    if (!(WEEK_DAY_KEYS as readonly string[]).includes(key)) continue;
    if (!byDay.has(key)) byDay.set(key, entry);
  }

  if (byDay.size === 0) return [...hours];

  return WEEK_DAY_KEYS.map((key) => {
    const existing = byDay.get(key);
    if (existing) return existing;
    return { day: WEEK_DAY_LABELS[key], open_time: null, close_time: null } as T;
  });
}
